import React from "react";
import { Row, Col } from "antd";
import { BookyDynamicInput } from "../Forms/BookyDynamicInput";
import { BookyPhoneInput } from "../Forms/BookyPhoneInput";
import { BookyCheckboxList } from "../Forms/BookyCheckboxList";
import { BookyCheckbox } from "../Forms/BookyCheckbox";
import { BookyDynamicCheckbox } from "../Forms/BookyDynamicCheckbox";
import { MemberFieldTypes } from "../../Types/MemberFieldTypes";
import { UI_CONTROLS } from "../../Types/FieldTypes";
import { OneFrontDatePicker } from "./BookyDatePicker";
import { BookySelectDropDown } from "./BookySelectDropDown";
import { PropertySafetyFilled } from "@ant-design/icons";
import { WebApiModelsMemberField, WebApiModelsMembershipProduct } from "../../Repository/eventbookyapi";
import { MembershipProductType } from "../../Types/MembershipProductType";

const isPairRow = (field: any, nextField: any) => {
    if (!field || !nextField) return false;
    const pairs = [
        [MemberFieldTypes.FirstName, MemberFieldTypes.LastName],
        [MemberFieldTypes.LastName, MemberFieldTypes.FirstName],
        [MemberFieldTypes.PostalArea, MemberFieldTypes.PostalCode],
        [MemberFieldTypes.PostalCode, MemberFieldTypes.PostalArea],
        [MemberFieldTypes.Email, MemberFieldTypes.Email2],
    ];
    return pairs.some(
        ([a, b]) => field.columnType === a && nextField.columnType === b
    );
};


// Props: memberFieldSettings, GetText, groups, user, onPhoneChange
export const OutPutMemberEditFields = ({
    memberFieldSettings,
    membershipProducts,
    GetText,
    groups,
    user,
    onPhoneChange,
    isAdmin
}: {
    memberFieldSettings: WebApiModelsMemberField[],
    membershipProducts: WebApiModelsMembershipProduct[],
    GetText: (key: string) => string,
    groups: any[],
    user: any,
    onPhoneChange: (value: any) => void,
    isAdmin: boolean
}) => {
    const fields = [...memberFieldSettings].sort((a, b) => (a.columnOrder ?? 0) - (b.columnOrder ?? 0));
    const result: JSX.Element[] = [];
    let i = 0;

    while (i < fields.length) {
        const field = fields[i];
        const nextField = fields[i + 1];

        // Default spans
        let firstSpan = 12;
        let secondSpan = 12;

        // Special case: postalcode should be smaller (span 8), the other 16
        if (
            (field.columnType === MemberFieldTypes.PostalArea && nextField.columnType === MemberFieldTypes.PostalCode) ||
            (field.columnType === MemberFieldTypes.PostalCode && nextField.columnType === MemberFieldTypes.PostalArea)
        ) {
            firstSpan = field.columnType === MemberFieldTypes.PostalCode ? 8 : 16;
            secondSpan = nextField.columnType === MemberFieldTypes.PostalCode ? 8 : 16;
        }
        if (isPairRow(field, nextField)) {

            result.push(
                <Row gutter={16} key={`${field.id}-${nextField.id}`}>
                    <Col span={firstSpan}>
                        <BookyDynamicInput
                            key={field.id}
                            fieldType={field.columnType}
                            fieldId={field.id}
                            isAdmin={isAdmin}
                            memberFieldSettings={memberFieldSettings}
                            validateEmailIfFilledIn={false}
                        />
                    </Col>
                    <Col span={secondSpan}>
                        <BookyDynamicInput
                            key={nextField.id}
                            fieldType={nextField.columnType}
                            fieldId={field.id}
                            isAdmin={isAdmin}
                            memberFieldSettings={memberFieldSettings}
                            validateEmailIfFilledIn={false}
                        />
                    </Col>
                </Row>
            );
            i += 2;
            continue;
        }

        // Render all other fields as before
        if (field.columnType == MemberFieldTypes.Custom) {
            result.push(
                <BookyDynamicInput
                    key={field.id}
                    fieldId={field.id}
                    fieldType={field.columnType}
                    isAdmin={isAdmin}
                    memberFieldSettings={memberFieldSettings}
                    validateEmailIfFilledIn={false}
                />
            );
        }
        else if (field.columnType === MemberFieldTypes.PhoneNumber || field.columnType === MemberFieldTypes.PhoneNumber2) {
            if (field.visibleInAdmin && isAdmin || field.visiblePublic && !isAdmin) {
                result.push(
                    <BookyPhoneInput
                        key={field.id}
                        data-testid={field.columnType === MemberFieldTypes.PhoneNumber2 ? "phone-number-2-input" : "phone-number-input"}
                        label={GetText("EditMember_PhoneNumber") + (field.columnType === MemberFieldTypes.PhoneNumber2 ? " 2" : "")}
                        disabled={user.hasOnlyReadRights}
                        name="phoneNumber"
                        onchange={onPhoneChange}
                        req
                        placeholder={""}
                    />
                );
            }
        }
        else if (field.columnType === MemberFieldTypes.JoinedDate) {
            if (field.visibleInAdmin && isAdmin || field.visiblePublic && !isAdmin) {
                result.push(
                    <OneFrontDatePicker
                        key={field.id}
                        label={GetText("EditMember_JoinedDate")}
                        name="joinedDate"
                        placeholder={GetText("EditMember_JoinedDatePlaceHolder")}
                    />
                );
            }
        }
        else if (field.columnType == MemberFieldTypes.Membership) {
            if (field.visibleInAdmin && isAdmin || field.visiblePublic && !isAdmin && membershipProducts.filter(mp => mp.membershipProductType == MembershipProductType.MembershipType).length > 0) {
                result.push(
                    <BookySelectDropDown
                        key={field.id}
                        allowClear={true}
                        handleChange={() => { }}
                        label={GetText("EditMember_Membership")}
                        name="membership"
                        options={membershipProducts.filter(mp => mp.membershipProductType == MembershipProductType.MembershipType).map(mp => ({ label: mp.name, value: mp.id }))}
                    />
                );
            }
        }
        else if (field.columnType == MemberFieldTypes.MembershipAddon) {
            if (field.visibleInAdmin && isAdmin || field.visiblePublic && !isAdmin && membershipProducts.filter(mp => mp.membershipProductType == MembershipProductType.Addon).length > 0) {
                result.push(
                    <BookyCheckboxList
                        key={field.id}
                        label={GetText("EditMember_MembershipAddon")}
                        name="membershipAddon"
                        options={membershipProducts.filter(mp => mp.membershipProductType == MembershipProductType.Addon).map(mp => ({ label: mp.name, value: mp.id }))}
                    />
                );
            }
        }
        else if (field.columnType === MemberFieldTypes.EmailNotificationPreference) {
            if (field.visibleInAdmin && isAdmin || field.visiblePublic && !isAdmin) {
                result.push(
                    <BookyDynamicCheckbox
                        key={field.id}
                        fieldId={field.id}
                        fieldType={MemberFieldTypes.EmailNotificationPreference}
                        isAdmin={isAdmin}
                        memberFieldSettings={memberFieldSettings}
                    />
                );
            }
        } else {
            result.push(
                <BookyDynamicInput
                    key={field.id}
                    fieldId={field.id}
                    fieldType={field.columnType}
                    isAdmin={isAdmin}
                    memberFieldSettings={memberFieldSettings}
                    validateEmailIfFilledIn={
                        field.columnType === MemberFieldTypes.Email ||
                        field.columnType === MemberFieldTypes.Email2
                    }
                />
            );
        }
        i++;
    }

    if (isAdmin) {
        result.push(
            <BookyCheckboxList
                key={"groups"}
                text={GetText("EditMember_Groups")}
                options={groups}
                name="groups"
                req
                placeholder={GetText("EditMember_Groups")}
            />
        );

        result.push(
            <BookyCheckbox
                key={"status"}
                alignLabelRight={true}
                label={"Avslutad"}
                name="status"
            />
        );
    }


    return <>{result}</>;
};